import React from "react";
import SideMenu from "../common/SideMenu";
import { OrganizationResponseDto } from "../common/ObjectTypes";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import tailwindClassnames from "../common/TailwindClassnames";
import Pagination from "../common/Pagination";

type OrganizationsListProps = {
  organizations: OrganizationResponseDto[];
  totalCount: number;
  take: number;
  offset: number;
  onPageClicked: Function;
  countries: any[];
  onSelectOrg: Function;
  onAddOrg: Function;
  getParentOrgName: Function;
  loading: boolean;
  onChangeSearchQuery: Function;
  onSearch: Function;
  tempOrganizationsSearchQuery: any;
  resultsLoaded: boolean;
  hasOrganizations: boolean;
};

/**
 * Organizations list page: page header (title + subtitle + add
 * action) above a table card with an attached filter toolbar, quiet caps
 * column headers, and pagination in the card footer.
 */
// Widened variant of the shared filter-input width, local to this toolbar so the
// name/email filters get extra room without affecting the `tableFilterInput`
// class used by other table toolbars.
const wideTableFilterInput = tailwindClassnames.tableFilterInput.replace(
  "w-44",
  "w-56",
);

const OrganizationsList = ({
  organizations,
  totalCount,
  take,
  offset,
  onPageClicked,
  countries,
  onSelectOrg,
  onAddOrg,
  getParentOrgName,
  loading,
  onChangeSearchQuery,
  onSearch,
  tempOrganizationsSearchQuery,
  resultsLoaded,
  hasOrganizations,
}: OrganizationsListProps) => {
  const { t } = useTranslation();
  const getCountryName = (countryCode: any) => {
    const country = countries.find((country) => country.code === countryCode);
    return country ? country.name : "Country not found";
  };

  return (
    <div className="flex flex-row w-full">
      <SideMenu />
      <div className="overflow-x-auto w-full mx-8 mt-8">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div>
            <h1 className={tailwindClassnames.pageTitle}>
              {t("Organizations")}
            </h1>
            <p className={tailwindClassnames.pageSubtitle}>
              {t("Manage organizations, their members, and sensors")}
            </p>
          </div>
        </div>

        <div className={tailwindClassnames.tableCard}>
          <div className={tailwindClassnames.tableCardToolbar}>
            <TextInput
              name={"org_name"}
              label={t("Name")}
              placeholder={t("Search organization name")}
              value={tempOrganizationsSearchQuery.name}
              error={""}
              disabled={false}
              className={wideTableFilterInput}
              type={"text"}
              initialFocus={true}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <TextInput
              name={"primary_contact_email"}
              label={t("Email")}
              placeholder={t("Search email")}
              value={tempOrganizationsSearchQuery.primary_contact_email}
              error={""}
              disabled={false}
              className={wideTableFilterInput}
              type={"text"}
              initialFocus={false}
              onChange={(e: any) => onChangeSearchQuery(e)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <SelectInput
              options={countries.map((country) => ({
                value: country.code,
                label: country.name,
              }))}
              className={"w-48 text-left text-sm"}
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isSearchable={true}
              isMulti={false}
              name={t("Country")}
              onChange={(e: any) =>
                onChangeSearchQuery(
                  e
                    ? {
                        target: {
                          name: "country_id",
                          value: e.value,
                        },
                      }
                    : null,
                )
              }
              errors={""}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
              searchBar={true}
            />
            <button
              className={tailwindClassnames.btnFilterApply}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                onSearch(event)
              }
            >
              {t("Apply filters")}
            </button>
            <div className="flex-1" />
            {/* Anyone signed in may create an organization (Akili-Wiki#595). */}
            <button
              className={tailwindClassnames.btnPrimary}
              onClick={() => onAddOrg()}
            >
              <span aria-hidden="true">+ </span>
              {t("Add Organization")}
            </button>
          </div>

          {loading ? (
            <div className="sweet-loading flex justify-center w-full py-8">
              <BeatLoader
                color={tailwindClassnames.skyBlueDark}
                loading={true}
                size={15}
                margin={5}
              />
            </div>
          ) : hasOrganizations ? (
            <>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {t("Organization")}
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {t("Primary Contact")}
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {t("Country")}
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}>
                      {t("Parent Organization")}
                    </th>
                  </tr>
                </thead>
                {resultsLoaded ? (
                  <tbody>
                    {organizations.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
                        >
                          {t("No results found")}
                        </td>
                      </tr>
                    )}
                    {organizations.map((org, index) => {
                      const countryName = getCountryName(org.country_id);
                      const parentOrgName = getParentOrgName(
                        org.parent_organization_id,
                      );
                      return (
                        <tr
                          key={index}
                          onClick={() => onSelectOrg(org.guid)}
                          className={tailwindClassnames.tableCardRow}
                        >
                          <td
                            className={tailwindClassnames.tableCardCellPrimary}
                            title={org.org_name ?? undefined}
                          >
                            {org.org_name}
                          </td>
                          <td
                            className={tailwindClassnames.tableCardCell}
                            title={org.primary_contact_email ?? undefined}
                          >
                            {org.primary_contact_email}
                          </td>
                          <td
                            className={tailwindClassnames.tableCardCell}
                            title={countryName || undefined}
                          >
                            {countryName}
                          </td>
                          <td
                            className={tailwindClassnames.tableCardCell}
                            title={parentOrgName || undefined}
                          >
                            {parentOrgName}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td
                        colSpan={4}
                        className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
                      >
                        {t("Loading...")}
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
              <Pagination
                showText={true}
                handlePageClick={onPageClicked}
                pageCount={Math.ceil(totalCount / take)}
                resultStart={offset + 1}
                initialPage={Math.floor(offset / take)}
                resultEnd={
                  offset + take < totalCount ? offset + take : totalCount
                }
                totalCount={totalCount}
                take={take}
                loading={false}
                mainclassname={tailwindClassnames.tableCardFooter}
              />
            </>
          ) : (
            <p className={`${tailwindClassnames.warningLabel} m-4 w-fit`}>
              {t("You do not have access to any organizations")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationsList;
